import { productUrls } from "../config/site";
import type { Product, SecurityModule, WorkflowStep } from "../types";

const baseProducts: Omit<Product, "url">[] = [
  {
    id: "bid",
    group: "业务增长",
    name: "智能标讯",
    plainName: "招标线索",
    status: "已上线",
    statusTone: "live",
    eyebrow: "标讯发现",
    title: "发现适合事务所的招标线索",
    description:
      "聚合审计、税务、评估/咨询等专业服务招标信息，帮助团队集中查找、快速筛选和持续跟进项目机会。",
    role: "市场 / 投标负责人",
    capabilities: ["业务类型筛选", "地区与时间筛选", "项目机会跟进"],
    action: "进入智能标讯",
  },
  {
    id: "proposal",
    group: "业务增长",
    name: "标书智作",
    plainName: "投标文件辅助",
    status: "已上线",
    statusTone: "live",
    eyebrow: "投标响应",
    title: "从读懂招标文件，到形成投标成果",
    description:
      "AI 辅助提取项目关键信息、资格条件、评分标准和材料要求，帮助团队搭建响应框架、复用机构资料并完善投标内容。",
    role: "投标负责人 / 项目团队",
    capabilities: ["文件要点提取", "响应要求梳理", "内容辅助制作"],
    action: "进入标书智作",
  },
  {
    id: "audit",
    group: "专业作业",
    name: "审计智作",
    plainName: "审计智能作业",
    status: "规划中",
    statusTone: "planned",
    eyebrow: "审计项目协作",
    title: "贯通审计计划、底稿执行与复核协作",
    description:
      "围绕项目计划、风险评估、审计程序执行、工作底稿编制与多级复核，统一项目资料、任务进度和复核意见，减少审计团队的协作断点。",
    role: "审计项目团队",
    capabilities: ["项目计划与分工", "底稿编制与执行", "复核意见协同"],
    action: null,
  },
  {
    id: "jia",
    group: "智能支持",
    name: "小加+",
    plainName: "财税专业 AI 助手",
    status: "规划中",
    statusTone: "planned",
    eyebrow: "日常专业工作助手",
    title: "让准则检索、资料整理与文档起草更高效",
    description:
      "面向会计、审计与税务人员，提供准则法规检索、机构知识查找、专业资料整理和文档起草支持，帮助团队缩短信息查找与初稿准备时间。",
    role: "合伙人 / 专业项目人员",
    capabilities: ["准则法规检索", "机构知识查找", "专业文档起草"],
    action: null,
  },
];

export const products: Product[] = baseProducts.map((product) => ({
  ...product,
  url: productUrls[product.id] ?? null,
}));

export const liveProducts = products.filter((p) => p.statusTone === "live");
export const plannedProducts = products.filter((p) => p.statusTone === "planned");

export const workflowSteps: WorkflowStep[] = [
  {
    code: "BID",
    type: "bid",
    product: "智能标讯",
    title: "发现标讯",
    description: "聚合多源公告，按地区、业务类型与截止时间筛选可跟进机会",
    input: "分散公告",
    output: "可跟进标讯",
    status: "已上线",
  },
  {
    code: "RFP",
    type: "proposal",
    product: "标书智作",
    title: "投标响应",
    description: "识别资格、评分与材料要求，形成结构清晰的投标准备",
    input: "招标文件",
    output: "响应成果",
    status: "已上线",
  },
  {
    code: "AUD",
    type: "audit",
    product: "审计智作",
    title: "审计协作",
    description: "统一计划分工、资料请求、底稿执行和复核意见，清晰跟进项目进度",
    input: "项目资料",
    output: "协作成果",
    status: "规划中",
  },
];

export const securityModules: SecurityModule[] = [
  {
    icon: "boundary",
    title: "数据范围",
    question: "产品需要处理哪些业务资料？",
    detail:
      "围绕具体产品能力说明所需的数据类型与使用目的，让团队在启用前了解处理范围，并据此判断是否符合机构管理要求。",
  },
  {
    icon: "retention",
    title: "保存与删除",
    question: "文件是否保留，如何管理与删除？",
    detail:
      "在对应产品说明中呈现文件保存、保留与删除规则，帮助事务所结合项目要求和内部制度组织使用。",
  },
  {
    icon: "access",
    title: "访问权限",
    question: "谁可以访问机构与项目资料？",
    detail:
      "按机构、成员与项目角色组织访问范围，使资料在授权空间内流转，并由机构管理人员结合职责配置权限。",
  },
  {
    icon: "boundary",
    title: "第三方服务",
    question: "产品会使用哪些模型或技术服务？",
    detail:
      "涉及模型、云服务或分析工具时，说明服务类型、使用目的与数据边界，便于事务所开展内部评估。",
  },
  {
    icon: "human",
    title: "专业责任",
    question: "AI 输出如何进入专业工作流程？",
    detail:
      "AI 用于辅助检索、整理、分析和文档工作；关键结论由具备相应职责的专业人员核验并作出判断。",
  },
  {
    icon: "review",
    title: "成果复核",
    question: "辅助成果如何被检查和确认？",
    detail:
      "辅助成果在进入业务流程前由专业人员核验，团队结合项目要求检查依据、完善内容并确认关键结论。",
  },
];

export function getProductById(id: string): Product | undefined {
  return products.find((product) => product.id === id);
}
